<template>
  <div class="filter-sec">
    <div class="filter-bar">
      <!-- ── Category chips — always visible, replaces the old pill tabs ─────── -->
      <div class="quick-category-row">
        <div class="quick-chips" ref="quickChips" @scroll="onChipsScroll">
          <button
            v-for="cat in categories.filter(c => c.value !== '')"
            :key="cat.value"
            class="quick-chip"
            :class="[`quick-chip--${cat.icon}`, { 'quick-chip--selected': activeCategory === cat.value }]"
            @click="selectCategory(cat.value)"
          >
            <!-- Icon -->
            <span class="quick-chip-icon">
              <svg v-if="cat.icon === 'rentals'" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
              <svg v-else-if="cat.icon === 'jobs'" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/><rect width="20" height="14" x="2" y="6" rx="2"/></svg>
              <svg v-else-if="cat.icon === 'sale'" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>
              <svg v-else-if="cat.icon === 'events'" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/><path d="m9 16 2 2 4-4"/></svg>
              <svg v-else-if="cat.icon === 'services'" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
            </span>
            <!-- Text -->
            <span class="quick-chip-body">
              <span class="quick-chip-name">{{ cat.label }}</span>
              <span class="quick-chip-sub">{{ cat.tagline }}</span>
            </span>
            <!-- Arrow (hidden when selected) -->
            <svg v-if="activeCategory !== cat.value" class="quick-chip-arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            <!-- Checkmark when selected -->
            <svg v-else class="quick-chip-check" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </button>
        </div>
        <!-- Scroll hint: visible only on mobile when more chips overflow right -->
        <span
          v-if="showScrollHint"
          class="scroll-hint"
          @click="nudgeChipsScroll"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
        </span>
      </div>

      <div class="filter-row top-row">
        <!-- Left: Title -->
        <p class="sec-title">
          <span v-if="activeSearchQuery" class="search-query-chip">
            🔍 "{{ activeSearchQuery }}"
            <span v-if="aiParsedIntent" class="ai-intent">
              → {{ aiParsedIntent.serviceType1 || '' }}{{ aiParsedIntent.serviceType2 ? ' > ' + aiParsedIntent.serviceType2 : '' }}{{ aiParsedIntent.city ? ' in ' + aiParsedIntent.city : '' }}{{ aiParsedIntent.state ? ', ' + aiParsedIntent.state : '' }}{{ aiParsedIntent.freeText ? ' (' + aiParsedIntent.freeText + ')' : '' }}
            </span>
            <button class="chip-clear" @click="clearSearchQuery">&times;</button>
          </span>
          <template v-else>
            <span class="subtype-text">{{ subType || 'Explore Listings' }}</span>
            <span class="location-text">near&nbsp;<span class="location-city">{{ displayLocation }}</span></span>
          </template>
        </p>

        <!-- Right: View toggles + Sort + Advance Search -->
        <div class="top-row-controls">
          <!-- View toggles -->
          <div class="inline-view-section">
            <div
              class="inline-view-btn"
              :class="{ active: currentView === 'list' }"
              @click="$emit('setView', 'list')"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/></svg>
              <span>List View</span>
            </div>
            <div
              class="inline-view-btn"
              :class="{ active: currentView === 'grid' }"
              @click="$emit('setView', 'grid')"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
              <span>Grid View</span>
            </div>
          </div>

          <!-- Sort -->
          <div class="inline-sort-btn" @click="$emit('toggleSort')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M7 4L7 20L4 17M7 20L10 17" stroke="currentColor" stroke-width="2" />
              <path d="M17 20V4L14 7M17 4L20 7" stroke="currentColor" stroke-width="2" />
            </svg>
            <span>{{ sortLabel }}</span>
          </div>

          <!-- Reset -->
          <div class="inline-sort-btn" @click="$emit('resetFilters')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M3 3V9H9" stroke="currentColor" stroke-width="2" />
              <path d="M21 21V15H15" stroke="currentColor" stroke-width="2" />
              <path d="M21 9C20 6 18 4 16 3C13 2 11 3 9 4M3 15C4 18 6 20 8 21C11 22 13 21 15 20" stroke="currentColor" stroke-width="2" />
            </svg>
            <span>Reset</span>
          </div>

          <!-- Photo visibility toggle: always visible, outside the advanced panel -->
          <div
            class="photo-toggle"
            role="tablist"
            aria-label="Filter by photo availability"
            :data-active="withPhotosOnly ? 'photos' : 'all'"
          >
            <span class="photo-toggle-slider" aria-hidden="true"></span>
            <button
              type="button"
              role="tab"
              class="photo-toggle-btn"
              :class="{ 'is-active': !withPhotosOnly }"
              :aria-selected="!withPhotosOnly"
              @click="setPhotoFilter(false)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
              <span>All Listings</span>
            </button>
            <button
              type="button"
              role="tab"
              class="photo-toggle-btn"
              :class="{ 'is-active': withPhotosOnly }"
              :aria-selected="withPhotosOnly"
              @click="setPhotoFilter(true)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>
              <span>Photos Only</span>
            </button>
          </div>

          <!-- Advance Search button -->
          <button
            class="SearchButton"
            @click="toggleAdvanced"
            v-if="!showAdvanced"
          >
            <img src="../icons/checkbox.svg" alt="icon" />
            <span>Advance Search</span>
          </button>

          <span class="hide-text" @click="toggleAdvanced" v-else>
            &times; <span class="hide-full">Hide Advance Search</span><span class="hide-short">Hide</span>
          </span>
        </div>
      </div>

      <div class="advanced-panel" v-if="showAdvanced">
        <div class="advanced-panel-inner">
          <div class="filter-row">
            <div class="search-field" style="position: relative">
              <label>Search Location</label>
              <div class="input-wrapper">
                <img src="../icons/search.svg" alt="search icon" />
                <div class="location-input-wrapper" ref="locationBox">
                  <input
                    type="text"
                    placeholder="Search"
                    v-model="searchQuery"
                    @input="onInputChange"
                    @focus="showSuggestions = true"
                  />
                  <ul v-if="showSuggestions && suggestions.length" class="suggestion-list">
                    <li
                      v-for="(place, index) in suggestions"
                      :key="place.place_id || place.description || index"
                      @mouseover="hoverIndex = index"
                      @mouseleave="hoverIndex = null"
                      @click="selectSuggestion(place)"
                      :class="{ highlighted: index === hoverIndex }"
                    >
                      <div class="suggestion-item">
                        <i class="fas fa-map-marker-alt location-icon"></i>
                        <span class="place-text">{{ place.description }}</span>
                        <i
                          v-if="isSelectedPlace(place)"
                          class="fas fa-check check-icon"
                        ></i>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="field">
              <label>Near</label>
              <SearchableSelect
                v-model="distance"
                :options="distanceOptions"
                placeholder="Select Distance"
              />
            </div>

            <div class="field">
              <label>Service Type</label>
              <SearchableSelect
                v-model="subType"
                :groups="serviceTypeGroups"
                placeholder="Select SubType"
              />
            </div>

            <!-- 3rd-level leaf (serviceTypeInfo) — only shown when the selected
                 Service Type has a Service_type3 mapping (e.g. Real Estate ›
                 Residential Properties › Single Family Homes). -->
            <div class="field" v-if="serviceTypeInfoOptions.length">
              <label>Property Type</label>
              <SearchableSelect
                v-model="subTypeInfo"
                :options="serviceTypeInfoOptions"
                placeholder="All Types"
                clearable
              />
            </div>

            <div class="field button-field">
              <button class="find-btn" @click="handleSearch('click')">
                <img src="../icons/Icons.svg" alt="searchicon" />
                <span>Find Result</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import serviceType from "./servicesType.json";
import { EventBus } from "../../services/EventBus.js";
import { watch } from "vue";
import locationService from "../../services/locationService.js";
import { trackEvent } from "@/composables/useAnalytics";
import { trackInteraction } from "@/utils/trackInteraction";
import SearchableSelect from "./Directory/SearchableSelect.vue";
export default {
  name: "AdvanceFilter",
  components: { SearchableSelect },
  props: {
    currentView: { type: String, default: "grid" },
    sortLabel: { type: String, default: "Newest" },
  },
  emits: ["setView", "toggleSort", "resetFilters"],
  watch: {
    // When the Service Type changes, drop any previously-picked property-type
    // leaf that no longer belongs to the new subtype (keeps it if still valid,
    // e.g. re-selecting the same parent after an EventBus sync).
    subType() {
      if (
        this.subTypeInfo &&
        !this.serviceTypeInfoOptions.some((o) => o.value === this.subTypeInfo)
      ) {
        this.subTypeInfo = "";
      }
    },
  },
  data() {
    return {
      searchQuery: "",
      _debounceTimer: null,
      distance: "",
      subType: "",
      subTypeInfo: "", // 3rd-level leaf (serviceTypeInfo), e.g. "Single Family Homes"
      suggestions: [],
      sortBy: "",
      selectedPlace: null,
      showAdvanced: false,
      withPhotosOnly: EventBus.withPhotosOnly === true,
      serviceTypes: serviceType.service_types,
      showSuggestions: false,
      hoverIndex: null,
      activeCategory: "",
      _searchPending: false,
      showScrollHint: false,
      categories: [
        { label: "All",      value: "",         icon: "all",      tagline: "" },
        { label: "Rentals",  value: "Rentals",  icon: "rentals",  tagline: "Rooms & apartments near you" },
        { label: "Jobs",     value: "Jobs",     icon: "jobs",     tagline: "Local employment listings" },
        { label: "Sale",     value: "Sale",     icon: "sale",     tagline: "Buy & sell locally" },
        { label: "Events",   value: "Events",   icon: "events",   tagline: "What's happening nearby" },
        { label: "Services", value: "Services", icon: "services", tagline: "Local help & experts" },
      ],
    };
  },

  created() {
    // Pre-populate searchQuery BEFORE the first render so the template never
    // displays the raw {lat,lng,label} object even for a single frame.
    const loc = EventBus.selectedLocation;
    const normalized = this.normalizeLocationLabel(loc);
    if (normalized) {
      this.searchQuery = normalized;
    }
  },

  mounted() {
    // Only run on root page (no filter applied, no path) AND no filters active
    const isRoot = window.location.pathname === '/' && !window.location.search && !window.location.hash;
    const noFilters =
      !this.searchQuery &&
      !this.distance &&
      !this.subType &&
      !this.activeCategory &&
      !this.sortBy;
    if (isRoot && noFilters) {
      this.loadDefaultLocationIfEmpty();
    }
    document.addEventListener("click", this.handleLocationClickOutside);

    // Check if chips overflow and show scroll hint on mobile
    this.$nextTick(() => this.checkChipsOverflow());

    // Track whether the initial watcher flush is complete. During the first
    // tick (immediate watchers), we only sync UI state — we do NOT trigger a
    // search because Views.vue created() already handles the initial fetch.
    // After nextTick, subsequent EventBus changes (e.g. metadata refinement,
    // user interactions) will trigger searches normally.
    let initialFlush = true;
    this.$nextTick(() => { initialFlush = false; });

    // ── IMPORTANT: Register selectedServiceType FIRST so activeCategory is set
    // before the subtype/location watchers fire handleSearch(). All three EventBus
    // values are set synchronously by CitywisePage; Vue processes queued watchers
    // in registration order, so this guarantees activeCategory is populated.
    watch(
      () => EventBus.selectedServiceType,
      (newVal) => {
        // Sync the active category chip with EventBus
        this.activeCategory = newVal || "";

        // When coming from a hero card or menu: clear any stale subtype so the
        // category-click searches the WHOLE category, then trigger the search.
        // (This previously auto-selected the category's first subtype — now
        // that the subtype is honored by the query, that would wrongly narrow
        // a plain category click down to a single subtype.)
        if (newVal && EventBus.comingFromMenu && !initialFlush) {
          this.subType = "";
          this.scheduleSearch();
        }
        // When coming from a URL (CitywisePage): activeCategory is now set; the
        // location watcher will call handleSearch() with the full context shortly.
      },
      { immediate: true }
    );

    watch(
      () => EventBus.selectedSubtype,
      (newVal) => {
        if (newVal) {
          // Slug → label may produce "Find A Room" but the JSON has "Find a Room".
          // Look up the exact-case value from servicesType.json so the <select>
          // v-model binding matches a real <option>.
          const exactMatch = this.serviceTypes
            .flatMap(st => st.Service_type2)
            .find(sub => sub.toLowerCase() === newVal.toLowerCase());
          this.subType = exactMatch || newVal;
          if (!initialFlush) this.scheduleSearch();
        }
      },
      { immediate: true }
    );

    // 3rd-level leaf sync: when a leaf is chosen from the nav mega-menu,
    // setServiceInfo() clears selectedSubtype, so first restore the parent
    // subtype (so the Property Type dropdown renders), then apply the leaf.
    watch(
      () => EventBus.selectedServiceInfo,
      (newVal) => {
        if (newVal) {
          const parent = this.findParentSubtype(newVal);
          if (parent && this.subType !== parent) this.subType = parent;
          this.subTypeInfo = newVal;
          if (!initialFlush) this.scheduleSearch();
        } else {
          this.subTypeInfo = "";
        }
      },
      { immediate: true }
    );

    watch(
      () => EventBus.selectedLocation,
      (newLoc) => {
        if (!newLoc) return;
        // Always store a plain string — never assign the {lat,lng,label} object to searchQuery
        const locString = this.normalizeLocationLabel(newLoc);
        if (!locString) return;
        // Normalize for comparison so cosmetic label refinements
        // (e.g. "Bridgewater Township Nj" → "Bridgewater Township, NJ")
        // update the display but don't trigger a duplicate search.
        const norm = s => s.toLowerCase().replace(/[,\s]+/g, ' ').trim();
        const isMeaningfulChange = norm(locString) !== norm(this.searchQuery);
        this.searchQuery = locString;  // always update display
        if (isMeaningfulChange && !initialFlush) this.scheduleSearch();
      },
      { immediate: true }
    );

    watch(
      () => EventBus.openAdvanceFilter,
      (val) => {
        if (val) {
          this.showAdvanced = true;
          EventBus.openAdvanceFilter = false;
        }
      }
    );

    watch(
      () => EventBus.resetFilters,
      (val) => {
        if (val) {
          this.resetAllFilters();
          EventBus.resetFilters = false;
        }
      }
    );

    // Sync the photo-toggle UI when restored from sessionStorage or EventBus
    // (e.g. dashboard state restore). Skip during initialFlush so we don't
    // double-fire handleSearch — Views.created() already owns the initial fetch.
    watch(
      () => EventBus.withPhotosOnly,
      (newVal) => {
        const normalized = newVal === true;
        if (this.withPhotosOnly !== normalized) {
          this.withPhotosOnly = normalized;
        }
      }
    );

    watch(
      () => EventBus.selectedSortBy,
      (newVal) => {
        if (newVal) {
          this.sortBy = newVal;
          this.handleSearch();
        }
      }
    );

    watch(
      () => EventBus.selectedCountryName,
      (newCountry) => {
        if (newCountry) {
          this.handleSearch();
        }
      }
    );

    // Watch for distance changes from hero cards
    watch(
      () => EventBus.selectedDistance,
      (newVal) => {
        if (newVal) {
          this.distance = newVal;
        }
      },
      { immediate: true }
    );

    // ── AI search → filter UI sync ──────────────────────────────────────────
    // When a natural-language nav search lands, Views.vue mirrors the parsed
    // intent into EventBus.parsedIntent. Apply it straight onto the visible
    // filter controls here so the category chip, Service Type dropdown and
    // Location box always reflect the words the AI matched — independent of
    // the per-field watchers above. Idempotent: safe to re-run on every search.
    watch(
      () => EventBus.parsedIntent,
      (intent) => {
        if (!intent) return;
        // Main category → highlighted chip
        if (intent.serviceType1) {
          this.activeCategory = intent.serviceType1;
        }
        // Sub-type → Service Type dropdown. Match the exact-case label from
        // servicesType.json so the <select> v-model binds a real <option>.
        if (intent.serviceType2) {
          const exact = this.serviceTypes
            .flatMap((st) => st.Service_type2)
            .find((s) => s.toLowerCase() === intent.serviceType2.toLowerCase());
          this.subType = exact || intent.serviceType2;
        }
        // Location → Location box. When the query names no city/state, fall
        // back to "USA" (the default nationwide search scope). handleSearch
        // treats a bare "USA" as no location filter, so this is display-safe.
        const loc = [intent.city, intent.state].filter(Boolean).join(", ");
        this.searchQuery = loc || "USA";
      },
      { immediate: true }
    );
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleLocationClickOutside);
  },

  computed: {
    activeSearchQuery() {
      return EventBus.searchQuery || "";
    },
    aiParsedIntent() {
      return EventBus.parsedIntent || null;
    },
    // Always expose the full service-type list so the Service Type dropdown
    // shows every category/subtype regardless of which hero button or category
    // chip the user clicked. Narrowing the dropdown to match activeCategory
    // confused users who wanted to switch categories from inside the filter.
    filteredServiceTypes() {
      return this.serviceTypes;
    },
    serviceTypeGroups() {
      return this.filteredServiceTypes.map((item) => ({
        label: item.Service_type1,
        options: item.Service_type2.map((sub) => ({ value: sub, label: sub })),
      }));
    },
    // 3rd-level (Service_type3) leaves for the currently selected Service Type.
    // Empty for subtypes with no leaf mapping — the Property Type dropdown then
    // stays hidden. e.g. subType "Residential Properties" → Single Family Homes…
    serviceTypeInfoOptions() {
      if (!this.subType) return [];
      for (const st of this.serviceTypes) {
        const map = st.Service_type3;
        if (map && Array.isArray(map[this.subType])) {
          return map[this.subType].map((leaf) => ({ value: leaf, label: leaf }));
        }
      }
      return [];
    },
    distanceOptions() {
      return [
        { value: 5, label: "5 Miles" },
        { value: 10, label: "10 Miles" },
        { value: 20, label: "20 Miles" },
        { value: 30, label: "30 Miles" },
        { value: 50, label: "50 Miles" },
        { value: 100, label: "100 Miles" },
      ];
    },

    // Display label for the headline
    displayLocation() {
      if (this.searchQuery) {
        return typeof this.searchQuery === 'string'
          ? this.searchQuery
          : (this.searchQuery?.label || 'your Location');
      }
      const loc = EventBus.selectedLocation;
      if (typeof loc === 'string' && loc) return loc;
      if (loc && typeof loc === 'object' && loc.label) return loc.label;
      // AI search with no specific city/state → default search scope is USA.
      if (EventBus.parsedIntent) return 'USA';
      return 'your Location';
    },

    exploreText() {
      if (this.subType) {
        return `${this.subType} - Explore Near by your Location`;
      }
      return "Explore Near by your Location";
    },
  },

  methods: {
    clearSearchQuery() {
      EventBus.clearSearchQuery();
      // Re-trigger normal geo-based search
      this.scheduleSearch();
    },

    // Coalesce multiple watcher-triggered searches into a single call.
    // All immediate watchers fire synchronously; nextTick ensures state
    // is settled before the one actual search fires.
    scheduleSearch() {
      if (this._searchPending) return;
      this._searchPending = true;
      this.$nextTick(() => {
        if (!this._searchPending) return;
        this._searchPending = false;
        // AI search just mirrored parsedIntent into EventBus — the watchers
        // fire to sync the UI but the fetch already ran. Swallow this one.
        if (EventBus.suppressNextAutoSearch) {
          EventBus.suppressNextAutoSearch = false;
          return;
        }
        this.handleSearch();
      });
    },
    checkChipsOverflow() {
      const el = this.$refs.quickChips;
      if (!el) return;
      this.showScrollHint = el.scrollWidth > el.clientWidth + 10;
    },
    onChipsScroll() {
      const el = this.$refs.quickChips;
      if (!el) return;
      // Hide hint once user has scrolled near the end
      const remaining = el.scrollWidth - el.scrollLeft - el.clientWidth;
      this.showScrollHint = remaining > 30;
    },
    nudgeChipsScroll() {
      const el = this.$refs.quickChips;
      if (!el) return;
      el.scrollBy({ left: 150, behavior: 'smooth' });
    },
    handleLocationClickOutside(event) {
      const locationBox = this.$refs.locationBox;
      if (locationBox && !locationBox.contains(event.target)) {
        this.showSuggestions = false;
      }
    },

    async loadDefaultLocationIfEmpty() {
      if (this.searchQuery || EventBus.selectedLocation) return;
      const userId = localStorage.getItem("userid");
      if (!userId) return;
      const defaultLoc = await locationService.loadDefaultLocation();
      if (defaultLoc && defaultLoc.city) {
        EventBus.selectedLocation = defaultLoc.city;
      }
    },

    getCityFromLocation(location) {
      if (!location) return "";
      return location.split(",")[0].trim();
    },

    // Given a 3rd-level leaf (e.g. "Single Family Homes"), find its parent
    // Service_type2 (e.g. "Residential Properties") so the dependent dropdown
    // can render. Case-insensitive; returns "" if the leaf isn't found.
    findParentSubtype(leaf) {
      if (!leaf) return "";
      const target = leaf.toLowerCase();
      for (const st of this.serviceTypes) {
        const map = st.Service_type3;
        if (!map) continue;
        for (const sub of Object.keys(map)) {
          if (map[sub].some((l) => l.toLowerCase() === target)) return sub;
        }
      }
      return "";
    },

    normalizeLocationLabel(loc) {
      if (!loc) return "";
      if (typeof loc === "string") return loc.trim();
      if (typeof loc === "object") {
        return (
          loc.label ||
          [loc.city, loc.region].filter(Boolean).join(", ") ||
          loc.fullAddress ||
          loc.city ||
          ""
        );
      }
      return "";
    },

    resetAllFilters() {
      this.isResetting = true;
      this.searchQuery = "";
      this.distance = "";
      this.subType = "";
      this.subTypeInfo = "";
      this.activeCategory = "";
      this.selectedPlace = null;
      this.suggestions = [];
      this.showSuggestions = false;
      this.withPhotosOnly = false;
      EventBus.withPhotosOnly = false;
      EventBus.selectedSortBy = "";
      EventBus.selectedSubtype = "";
      EventBus.selectedServiceInfo = "";
      EventBus.selectedServiceType = "";
      EventBus.selectedLocation = "";
      EventBus.selectedCountryName = "";

      this.handleSearch();
      this.isResetting = false;
    },

    async fetchSuggestions() {
      if (this.searchQuery.length < 2) {
        this.suggestions = [];
        return;
      }
      try {
        const response = await axios.post("/api/places/autocomplete", {
          input: this.searchQuery,
        });
        this.suggestions = response.data || [];
      } catch (err) {
        console.error("Error fetching suggestions:", err);
      }
    },

    toggleAdvanced() {
      // Only count the click that OPENS the advanced panel, not the collapse.
      if (!this.showAdvanced) {
        trackInteraction('search', 'advanced-search');
      }
      this.showAdvanced = !this.showAdvanced;
    },

    onInputChange() {
      if (
        this.selectedPlace &&
        this.searchQuery !== (this.selectedPlace.description || "")
      ) {
        this.selectedPlace = null;
      }
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => this.fetchSuggestions(), 500);
    },

    selectSuggestion(place) {
      this.selectedPlace = place;
      this.searchQuery = place.description || "";
      this.suggestions = [];
      this.showSuggestions = false;
      this.hoverIndex = null;
    },

    isSelectedPlace(place) {
      if (!this.selectedPlace) return false;
      if (this.selectedPlace.place_id && place.place_id) {
        return this.selectedPlace.place_id === place.place_id;
      }
      return (
        (this.selectedPlace.description || "") === (place.description || "")
      );
    },

    selectCategory(value) {
      // Toggle: clicking the already-active chip resets to "All"
      const next = this.activeCategory === value ? '' : value;
      this.activeCategory = next;
      // Clear AI search so category filtering takes over
      EventBus.clearSearchQuery();
      EventBus.userInteracted = true;
      EventBus.selectedServiceType = next;
      EventBus.selectedSubtype = "";
      EventBus.selectedServiceInfo = "";
      this.subType = "";
      this.subTypeInfo = "";
      trackEvent('select_category', { category: next || 'All' });
      this.handleSearch();
    },
 setPhotoFilter(value) {
      if (this.withPhotosOnly === value) return;
      this.withPhotosOnly = value;
      EventBus.withPhotosOnly = value;
      trackEvent('toggle_photo_filter', { with_photos_only: value });
      this.handleSearch();
    },

    // `trigger` is 'click' ONLY from the Find Result button. Every other caller
    // is a watcher (category/subtype/location changed, reset, scheduleSearch)
    // and leaves it at 'auto'. Without that distinction the Clicks report would
    // count each automatic re-fetch as a user-run advanced search — this method
    // fires far more often from watchers than from the button.
    handleSearch(trigger = 'auto') {
      // While Views is restoring saved state (back-from-detail), it drives the
      // fetch itself. A watcher-triggered search here would race that restore
      // and overwrite the restored results/filters — so swallow it.
      if (EventBus.restoreInProgress) return;
      console.log("pressed");
      console.log(this.searchQuery);
      console.log("Distance:", this.distance);
      console.log("Service Sub Type:", this.subType);
      console.log("sortBy", this.sortBy);
      // Clear AI search so normal filter takes over
      if (EventBus.searchQuery) {
        EventBus.clearSearchQuery();
      }
      EventBus.userInteracted = true;
      EventBus.selectedSubtype = this.subType;
      // 3rd-level leaf — dashboardService prefers serviceTypeInfo over
      // serviceType2/1, so an empty value cleanly falls back to the subtype.
      EventBus.selectedServiceInfo = this.subTypeInfo;
      // A bare "USA" in the Location box means "search the whole country" —
      // treat it as no location filter so it isn't geocoded to a single point.
      const isUsaWide =
        typeof this.searchQuery === "string" &&
        this.searchQuery.trim().toLowerCase() === "usa";
      const effectiveLoc = isUsaWide ? "" : this.searchQuery;
      if (!this.isResetting && effectiveLoc && !this.distance) {
        this.distance = 30;
      }
      // Mirror distance into EventBus so the singleton stays in sync with the
      // dropdown — Views.persistState owns sessionStorage, but EventBus is the
      // source of truth for in-memory cross-component sync (e.g. AdvanceFilter
      // re-mounts after back-from-detail).
      EventBus.selectedDistance = this.distance;
      // Extract coords from EventBus BEFORE overwriting it with the plain string
      // Only reuse those coords when they belong to the place in the Location
      // box. After the user picks a different place (Google suggestions carry
      // no _lat/_lng), the old coords would search the previous city under
      // the new label — wrong results and count. Without coords,
      // getDashboardData geocodes the typed label instead.
      const loc = EventBus.selectedLocation;
      const norm = (s) => String(s || "").toLowerCase().replace(/[,\s]+/g, " ").trim();
      const locIsCurrent =
        loc && typeof loc === 'object' &&
        norm(this.normalizeLocationLabel(loc)) === norm(effectiveLoc);
      const locLat = locIsCurrent ? loc.lat : null;
      const locLng = locIsCurrent ? loc.lng : null;
      // Also check selectedPlace from autocomplete pick within AdvanceFilter itself
      const placeLat = this.selectedPlace?._lat || null;
      const placeLng = this.selectedPlace?._lng || null;
      // Now sync the display string to EventBus
      if (effectiveLoc && EventBus.selectedLocation !== effectiveLoc) {
        // Preserve coords if we have them
        const lat = placeLat || locLat;
        const lng = placeLng || locLng;
        if (lat && lng) {
          EventBus.selectedLocation = { lat, lng, label: effectiveLoc };
        } else {
          EventBus.selectedLocation = effectiveLoc;
        }
      }
      const filters = {
        userinput: effectiveLoc,
        distance: this.distance,
        selectedSubType: this.subType,
        selectedServiceInfo: this.subTypeInfo || "",
        selectedServiceType: this.activeCategory || "",
        sortBy: this.sortBy,
        preferredCountryName: EventBus.selectedCountryName || "",
        latOverride: placeLat || locLat || null,
        lngOverride: placeLng || locLng || null,
         withPhotosOnly: this.withPhotosOnly,
      };
      console.log(filters);
      // Only the Find Result button reaches the admin Clicks report. Watcher-
      // driven re-fetches are not user searches and would swamp the collection.
      if (trigger === 'click') {
        trackInteraction('search', 'advanced-apply', {
          label: filters.userinput || '',
          extra: {
            category: filters.selectedServiceType || undefined,
            subType: filters.selectedSubType || undefined,
            serviceInfo: filters.selectedServiceInfo || undefined,
            distance: filters.distance || undefined,
            sortBy: filters.sortBy || undefined,
            withPhotosOnly: !!filters.withPhotosOnly,
            country: filters.preferredCountryName || undefined,
            hasCoords: !!(filters.latOverride && filters.lngOverride),
            filterCount: [
              filters.selectedServiceType,
              filters.selectedSubType,
              filters.selectedServiceInfo,
              filters.userinput,
              filters.withPhotosOnly ? 'photos' : '',
            ].filter(Boolean).length,
            trigger: 'click',
          },
        });
      }
      trackEvent('search', {
        search_term: this.searchQuery || '',
        category: this.activeCategory || 'All',
        sub_type: this.subType || '',
      });
      this.$emit("filter-applied", filters);
    },
  },
};
</script>

<style scoped>
/* ── Chip selected / active state ── */
.quick-chip--selected {
  border-color: currentColor !important;
  background: color-mix(in srgb, currentColor 14%, #fff) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.quick-chip--selected .quick-chip-icon {
  background: color-mix(in srgb, currentColor 24%, transparent);
}

.quick-chip--selected .quick-chip-name {
  color: inherit;
}

.quick-chip-check {
  flex-shrink: 0;
  opacity: 1;
}

.filter-sec {
  max-width: 1440px;
  width: 100%;
  margin-top: 36px;
  margin-left: auto;
  margin-right: auto;
}

.filter-bar {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  justify-content: center;
  box-sizing: border-box;
}

.sec-title {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 4px;
  font-family: var(--font-primary);
  letter-spacing: -0.02em;
}

.subtype-text {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
}

.location-text {
  font-size: 18px;
  font-weight: 500;
  color: #535862;
}

.location-city {
  color: #1a73e8;
  font-weight: 600;
}

.filter-sec .filter-row {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 21px;
  flex-wrap: nowrap;
  width: 100%;
}

.filter-row > div {
  flex: 0 0 auto;
  min-width: 0;
}

.search-field,
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.search-field label,
.field label {
  font-family: var(--font-primary);
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--color-text-secondary);
}

.input-wrapper {
  position: relative;
  width: 100%;
}

.input-wrapper img {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  pointer-events: none;
}

.input-wrapper input::placeholder {
  color: var(--color-text-tertiary);
}

.input-wrapper input {
  width: 418px;
  height: 40px;
  border: 1px solid var(--color-border-light);
  border-radius: 8px;
  padding-left: 34px;
  padding-right: 12px;
  font-size: 14px;
  outline: none;
  color: var(--color-text-primary);
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.input-wrapper input:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
}

.field select {
  width: 320px;
  height: 40px;
  border: 1px solid var(--color-border-primary);
  border-radius: 8px;
  padding-left: 12px;
  padding-right: 28px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  appearance: none;
  background: url("../icons/dropdown.svg") no-repeat right 10px center;
  background-size: 14px;
  background-color: var(--color-bg-primary);
  color: var(--color-text-primary);
}

.field :deep(.ss-root) { width: 320px; }
.field :deep(.ss-input) { height: 40px; }

.place-text {
  font-family: var(--font-primary);
  font-size: 14px;
  color: var(--color-text-primary);
}

.suggestion-list {
  position: absolute;
  top: 105%;
  left: 0;
  right: 0;
  background: var(--color-bg-primary);
  border-radius: 12px;
  border: 1px solid var(--color-border-light);
  box-shadow: 0px 6px 18px rgba(0, 0, 0, 0.05);
  max-height: 260px;
  overflow-y: auto;
  z-index: 50;
  padding: 4px 0;
  margin-top: 6px;
  list-style: none;
}

.suggestion-list li {
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.suggestion-list li.highlighted,
.suggestion-list li:hover {
  background-color: var(--color-bg-hover);
}

.suggestion-list li.selected {
  background-color: var(--color-bg-secondary);
}

.suggestion-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.location-icon {
  font-size: 16px;
  color: var(--color-text-primary);
}

.check-icon {
  font-size: 14px;
  color: var(--color-primary);
  margin-left: auto;
}

.find-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: linear-gradient(135deg, #3467d5 0%, #5b87f0 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 24px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  white-space: nowrap;
  min-width: 140px;
  height: 42px;
  transition: all 0.2s ease;
  font-family: var(--font-primary);
  box-shadow: 0 3px 10px rgba(52, 103, 213, 0.28);
}

.find-btn:hover {
  background: linear-gradient(135deg, #2857c0 0%, #4a76df 100%);
  box-shadow: 0 5px 16px rgba(52, 103, 213, 0.40);
  transform: translateY(-1px);
}

.find-btn img {
  width: 18px;
  height: 18px;
  filter: brightness(0) invert(1);
}

.button-field {
  flex: 0 0 auto;
}

.SearchButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(135deg, #3467d5 0%, #5b87f0 100%);
  color: #fff;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-family: var(--font-primary);
  font-size: 14px;
  line-height: 20px;
  box-shadow: 0 3px 10px rgba(52, 103, 213, 0.30);
  transition: all 0.2s ease;
  white-space: nowrap;
}

.SearchButton:hover {
  background: linear-gradient(135deg, #2857c0 0%, #4a76df 100%);
  box-shadow: 0 4px 14px rgba(52, 103, 213, 0.40);
  transform: translateY(-1px);
}

.SearchButton img {
  width: 18px;
  height: 18px;
  filter: brightness(0) invert(1);
}

.filter-row.top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.top-row-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.inline-view-section {
  display: flex;
  align-items: center;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.inline-view-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
  transition: all 0.15s ease;
  font-family: var(--font-primary);
}

.inline-view-btn:first-child {
  border-right: 1px solid #e5e7eb;
}

.inline-view-btn.active {
  background: #3467d5;
  color: #fff;
  border-radius: 8px;
}

.inline-view-btn.active svg {
  stroke: #fff;
}

.inline-view-btn:hover:not(.active) {
  background: #f3f4f6;
}

.inline-sort-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
  transition: all 0.15s ease;
  font-family: var(--font-primary);
}

.inline-sort-btn:hover {
  color: #111827;
}

/* Advanced Search Panel Card */
.advanced-panel {
  margin-top: 4px;
  animation: panel-slide-down 0.22s cubic-bezier(0.34, 1.20, 0.64, 1);
}

@keyframes panel-slide-down {
  from { opacity: 0; transform: translateY(-10px); }
  to   { opacity: 1; transform: translateY(0); }
}

.advanced-panel-inner {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 16px;
  padding: 20px 24px;
  box-sizing: border-box;
  box-shadow: 0 4px 20px rgba(52, 103, 213, 0.07), 0 1px 4px rgba(10, 13, 40, 0.05);
}

.advanced-panel .filter-row {
  width: 100%;
  justify-content: flex-start;
  align-items: flex-end;
}

.advanced-panel .search-field,
.advanced-panel .field {
  flex: 1 1 0;
  min-width: 0;
}

.advanced-panel .button-field {
  flex: 0 0 auto;
}

.advanced-panel .input-wrapper,
.advanced-panel .input-wrapper input {
  width: 100%;
  max-width: 100%;
}

.advanced-panel .field :deep(.ss-root) {
  width: 100%;
}

.advanced-panel .find-btn {
  white-space: nowrap;
}

@media (max-width: 1366px) {
  .filter-row {
    gap: 16px;
  }
}

@media (max-width: 1024px) {
  .filter-row {
    flex-wrap: wrap;
  }
  .filter-row > div {
    flex: 1 1 45%;
  }
  .button-field {
    flex: 1 1 100%;
  }
  .find-btn {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .filter-bar {
    padding: 0 16px;
  }
  .filter-row > div {
    flex: 1 1 100%;
  }
  .find-btn {
    width: 100%;
  }
  .filter-row.top-row {
    flex-wrap: wrap;
  }
  .top-row-controls {
    flex-wrap: wrap;
    gap: 6px;
  }
  .inline-view-section {
    order: 1;
  }
  .inline-sort-btn {
    padding: 6px 8px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .filter-sec {
    margin-top: 12px;
    padding: 0 14px;
  }

  .filter-bar {
    width: 100%;
    gap: 8px;
  }

  /* Section title — compact */
  .sec-title {
    gap: 4px;
    flex-wrap: nowrap;
    margin-bottom: 0;
    align-items: center;
  }

  .subtype-text {
    font-size: 18px;
    font-weight: 700;
  }

  .location-text {
    font-size: 13px;
  }

  .location-city {
    font-size: 13px;
  }

  /* Top row: title on top, controls below in a single compact row */
  .filter-row.top-row {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  /* Controls: photo-toggle on its own row, then compact button row */
  .top-row-controls {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background: #f8f9fb;
    border-radius: 10px;
    padding: 5px 6px;
    row-gap: 6px;
  }

  /* Reorder on mobile: Photo toggle(0, own row) → Sort(1) → Reset(2) → View(3) → Advance Search(4) */
  .inline-sort-btn {
    order: 1;
  }

  .inline-view-section {
    order: 3;
    border-radius: 8px;
    border-color: #e0e3ea;
    flex-shrink: 0;
    overflow: visible;
  }

  .SearchButton,
  .hide-text {
    order: 4;
  }

  .inline-view-btn {
    padding: 5px 8px;
    gap: 0;
  }

  .inline-view-btn span {
    display: none;
  }

  .inline-view-btn svg {
    width: 16px;
    height: 16px;
  }

  /* Sort & Reset — compact text buttons */
  .inline-sort-btn {
    padding: 5px 6px;
    font-size: 11px;
    gap: 3px;
    border-radius: 6px;
    flex-shrink: 0;
  }

  .inline-sort-btn svg {
    width: 13px;
    height: 13px;
  }

  .inline-sort-btn span {
    white-space: nowrap;
  }

  .inline-sort-btn:active {
    background: #e8ecf4;
  }

  /* Advance Search button — compact pill */
  .SearchButton {
    height: 30px;
    padding: 4px 8px;
    font-size: 11px;
    flex-shrink: 0;
    width: auto;
    border-radius: 8px;
    box-shadow: 0 2px 6px rgba(52, 103, 213, 0.25);
    gap: 4px;
  }

  .SearchButton span {
    font-size: 11px;
    line-height: 14px;
    width: auto;
    white-space: nowrap;
  }

  .SearchButton img {
    width: 13px;
    height: 13px;
  }

  /* Hide Advance Search link — compact */
  .hide-text {
    font-size: 11px;
    padding: 4px 6px;
    white-space: nowrap;
  }

  .hide-full {
    display: none;
  }

  .hide-short {
    display: inline;
  }

  /* ── Advanced filter panel — modern mobile layout ── */
  .advanced-panel {
    margin-top: 6px;
  }

  .advanced-panel-inner {
    padding: 14px 14px 16px;
    border-radius: 14px;
    background: linear-gradient(180deg, #ffffff 0%, #f7f9ff 100%);
    border: 1px solid #d8e0f5;
    box-shadow: 0 8px 24px rgba(52, 103, 213, 0.10),
                0 2px 6px rgba(10, 13, 40, 0.05);
  }

  /* Compact section header inside the panel */
  .advanced-panel-inner::before {
    content: 'Refine your search';
    display: block;
    font-family: var(--font-primary);
    font-size: 11px;
    font-weight: 700;
    color: #3467d5;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed #d8e0f5;
  }

  /* 2-col grid: location + button span full width; distance + service type pair up */
  .advanced-panel .filter-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 10px;
    align-items: stretch;
    flex-direction: unset;
    flex-wrap: unset;
  }

  .advanced-panel .search-field {
    grid-column: 1 / -1;
  }

  .advanced-panel .button-field {
    grid-column: 1 / -1;
    margin-top: 2px;
  }

  .advanced-panel .search-field,
  .advanced-panel .field {
    width: 100%;
    min-width: 0;
  }

  /* Compact uppercase labels — feel like form section headers */
  .advanced-panel .search-field label,
  .advanced-panel .field label {
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #6b7280;
    margin-bottom: 0;
  }

  /* Polished inputs / selects — bigger touch targets, focus ring */
  .advanced-panel .input-wrapper input,
  .advanced-panel .field select {
    width: 100%;
    height: 42px;
    border-radius: 10px;
    border-color: #d1d5db;
    font-size: 14px;
    background-color: #fff;
  }

  .advanced-panel .field select {
    background-position: right 10px center;
    padding-right: 28px;
  }

  .advanced-panel .field :deep(.ss-root) { width: 100%; }
  .advanced-panel .field :deep(.ss-input) { height: 42px; border-radius: 10px; }

  .advanced-panel .input-wrapper input:focus,
  .advanced-panel .field select:focus {
    border-color: #3467d5;
    box-shadow: 0 0 0 3px rgba(52, 103, 213, 0.15);
  }

  /* Find button — prominent CTA */
  .advanced-panel .find-btn {
    width: 100%;
    height: 46px;
    font-size: 15px;
    border-radius: 12px;
    letter-spacing: 0.01em;
    box-shadow: 0 4px 14px rgba(52, 103, 213, 0.32);
  }

  .advanced-panel .suggestion-list {
    width: 100%;
    font-size: 13px;
  }

  .filter-bar .filter-row {
    gap: 12px;
  }
}

.hide-text {
  font-family: var(--font-primary);
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #3467d5;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 8px;
  transition: background 0.15s ease, color 0.15s ease;
}

/* ✕ is rendered inline in the template */

.hide-text:hover {
  background: #f0f4ff;
  color: #2857c0;
}

.hide-short {
  display: none;
}

/* ══════════════════════════════════════════════════════
   QUICK-CATEGORY RECOMMENDATION ROW
══════════════════════════════════════════════════════ */
.quick-category-row {
  display: none;
}

.quick-chips {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

/* ── Individual chip ── */
.quick-chip {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 14px;
  border: 1.5px solid #e5e7eb;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.18s;
  min-width: 155px;
}

.quick-chip:hover {
  border-color: currentColor;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

/* ── Per-category accent colours ── */
.quick-chip--rentals  { color: #3b82f6; }
.quick-chip--jobs     { color: #16a34a; }
.quick-chip--sale     { color: #f59e0b; }
.quick-chip--events   { color: #db2777; }
.quick-chip--services { color: #7c3aed; }

/* ── Icon bubble ── */
.quick-chip-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: color-mix(in srgb, currentColor 12%, transparent);
  transition: background 0.2s;
}

.quick-chip:hover .quick-chip-icon {
  background: color-mix(in srgb, currentColor 20%, transparent);
}

.quick-chip-icon svg {
  width: 20px;
  height: 20px;
}

/* ── Text block ── */
.quick-chip-body {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
}

.quick-chip-name {
  font-family: var(--font-primary);
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}

.quick-chip-sub {
  font-family: var(--font-primary);
  font-size: 11.5px;
  color: #6b7280;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Arrow ── */
.quick-chip-arrow {
  flex-shrink: 0;
  opacity: 0.3;
  transition: opacity 0.2s, transform 0.2s;
}

.quick-chip:hover .quick-chip-arrow {
  opacity: 1;
  transform: translateX(3px);
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .quick-chips {
    gap: 8px;
  }
  .quick-chip {
    min-width: 150px;
    padding: 10px 12px;
    gap: 10px;
  }
  .quick-chip-icon {
    width: 32px;
    height: 32px;
  }
  .quick-chip-name { font-size: 13px; }
  .quick-chip-sub  { font-size: 11px; }
}

/* ── Scroll hint indicator (hidden on desktop) ── */
.scroll-hint { display: none; }

@media (max-width: 480px) {
  .quick-category-row {
    position: relative;
  }
  /* Gradient fade on right edge */
  .quick-category-row::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 40px;
    background: linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.92));
    pointer-events: none;
    z-index: 1;
  }
  /* Animated scroll-hint pill */
  .scroll-hint {
    display: flex;
    align-items: center;
    position: absolute;
    right: 2px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    background: rgba(52, 103, 213, 0.12);
    color: #3467d5;
    border: 1px solid rgba(52, 103, 213, 0.25);
    border-radius: 20px;
    padding: 4px 5px 4px 7px;
    cursor: pointer;
    animation: hintNudge 1.5s ease-in-out infinite;
    box-shadow: 0 2px 6px rgba(52, 103, 213, 0.12);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
  }
  .scroll-hint svg {
    display: block;
  }
  @keyframes hintNudge {
    0%, 100% { transform: translateY(-50%) translateX(0); }
    50%      { transform: translateY(-50%) translateX(4px); }
  }
  .quick-chips {
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: 6px;
    padding-bottom: 2px;
    padding-right: 30px;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }
  .quick-chips::-webkit-scrollbar { display: none; }
  .quick-chip {
    flex-shrink: 0;
    min-width: 0;
    padding: 6px 10px;
    border-radius: 10px;
    gap: 6px;
  }
  .quick-chip-sub { display: none; }
  .quick-chip-icon {
    width: 24px;
    height: 24px;
    border-radius: 7px;
  }
  .quick-chip-name { font-size: 12px; }
  .quick-chip-arrow,
  .quick-chip-check { width: 13px; height: 13px; }
}

@media (min-width: 1400px) {
  .input-wrapper input {
    max-width: 420px;
  }
  .field select {
    max-width: 300px;
  }
}

@media (min-width: 1266px) and (max-width: 1399px) {
  .input-wrapper input {
    max-width: 380px;
  }
  .field select {
    max-width: 290px;
  }
}

@media (min-width: 1200px) and (max-width: 1265px) {
  .input-wrapper input {
    max-width: 360px;
  }
  .field select {
    max-width: 280px;
  }
}

@media (min-width: 1024px) and (max-width: 1199px) {
  .input-wrapper input {
    max-width: 320px;
  }
  .field select {
    max-width: 260px;
  }
}

@media (max-width: 1023px) {
  .input-wrapper input,
  .field select {
    width: 100%;
  }
}

/* ── Search query chip ── */
.search-query-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #eef2ff;
  color: #3467d5;
  font-weight: 600;
  font-size: 15px;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid #c7d2fe;
}
.ai-intent {
  color: #059669;
  font-weight: 500;
  font-size: 13px;
}
.chip-clear {
  border: none;
  background: none;
  color: #6b7280;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  line-height: 1;
  padding: 0 2px;
}
.chip-clear:hover {
  color: #ef4444;
}

/* ── Photo visibility toggle (segmented pill, lives in top-row-controls) ── */
.photo-toggle {
  position: relative;
  display: inline-flex;
  padding: 4px;
  background: #f3f4f6;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
  isolation: isolate;
}

/* Sliding active pill */
.photo-toggle-slider {
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: 4px;
  width: calc(50% - 4px);
  background: #fff;
  border-radius: 999px;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.18),
              0 1px 2px rgba(0, 0, 0, 0.06);
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;
  pointer-events: none;
}

.photo-toggle[data-active="photos"] .photo-toggle-slider {
  transform: translateX(100%);
}

.photo-toggle-btn {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 14px;
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  border-radius: 999px;
  cursor: pointer;
  transition: color 0.2s ease;
  white-space: nowrap;
  flex: 1 1 0;
  min-width: 110px;
}

.photo-toggle-btn:hover {
  color: #374151;
}

.photo-toggle-btn.is-active {
  color: #2563eb;
}

.photo-toggle-btn svg {
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.photo-toggle-btn.is-active svg {
  transform: scale(1.08);
}

.photo-toggle-btn:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

@media (max-width: 540px) {
  .photo-toggle {
    width: 100%;
    flex: 1 0 100%;
    order: 0;
  }
  .photo-toggle-btn {
    padding: 7px 10px;
    font-size: 12px;
    min-width: 0;
  }
}

@media (max-width: 480px) {
  .photo-toggle {
    padding: 3px;
    border-radius: 10px;
  }
  .photo-toggle-slider {
    top: 3px;
    bottom: 3px;
    left: 3px;
    width: calc(50% - 3px);
    border-radius: 8px;
  }
  .photo-toggle-btn {
    padding: 6px 8px;
    font-size: 11px;
    border-radius: 8px;
    gap: 4px;
  }
  .photo-toggle-btn svg {
    width: 12px;
    height: 12px;
  }
}
</style>
